import React from 'react';
import { CertificateTemplate, CertificateTextField } from '../../../apis/api-client';
import { Box, Button, SelectField, Slider, TextField, Tooltip, Typography } from '../../ui';
import { FONT_OPTIONS, isCustomText, SYSTEM_FIELD_LABELS } from './constants';

/**
 * Controls panel for the currently-selected certificate field — content
 * (custom text only), font family/color, size, weight, and alignment.
 * When the selected field is a hidden system field, shows a hint to
 * re-enable it instead of the controls. All edits flow up via
 * `onUpdateField`.
 */
export default function CertificateFieldControls({
  template,
  selectedField,
  enabledFields,
  onUpdateField,
}: {
  template: CertificateTemplate;
  selectedField: string;
  enabledFields: Record<string, boolean>;
  onUpdateField: (fieldType: string, updates: Partial<CertificateTextField>) => void;
}) {
  const field = template.textFields.find((f) => f.fieldType === selectedField);
  const isCustom = isCustomText(selectedField);
  // Custom-text fields are always "enabled" — admins delete them
  // outright instead of hiding them.
  const isEnabled = isCustom ? !!field : enabledFields[selectedField];

  if (!isEnabled || !field) {
    return (
      <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{SYSTEM_FIELD_LABELS[selectedField] || 'This field'}</strong> is hidden on the certificate. Toggle the eye icon above to show it.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Custom-text content input — only for custom-text fields */}
      {isCustom && (
        <TextField
          label="Text"
          value={field.content || ''}
          onChange={(e) => onUpdateField(selectedField, { content: e.target.value })}
          size="small"
          fullWidth
          autoFocus
          placeholder="e.g. Continuing Education Credit"
        />
      )}

      {/* Hint for system fields — what this lookup pulls from */}
      {!isCustom && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {selectedField === 'CourseId' && 'Pulls from the Course ID set on the course Settings tab.'}
          {selectedField === 'CeHours' && 'Stamped with the course’s CE hours at issue time.'}
          {selectedField === 'AgdSubjectCode' && 'Pulls the numeric code from “AGD Subject Code” on the course CE Information.'}
          {selectedField === 'StudentName' && 'Pulls from the enrolled learner at issue time.'}
          {selectedField === 'CourseTitle' && 'Pulls from the course title.'}
          {selectedField === 'CompletionDate' && 'Stamped with the learner’s completion date.'}
        </Typography>
      )}

      {/* Row 1: Font family + color swatch */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* SelectField wraps MUI Autocomplete and takes an
            `options` prop (string[] ok) — NOT MenuItem children. */}
        <SelectField
          label="Font"
          value={field.fontFamily}
          onChange={(e) => onUpdateField(selectedField, { fontFamily: e.target.value })}
          options={FONT_OPTIONS}
          allowEmpty={false}
          size="small"
          sx={{ flex: 1 }}
        />
        <Tooltip title="Text color">
          <Box
            component="label"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: field.fontColor,
              cursor: 'pointer',
              flexShrink: 0,
              position: 'relative',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Box
              component="input"
              type="color"
              value={field.fontColor}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onUpdateField(selectedField, { fontColor: e.target.value })
              }
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'pointer',
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      {/* Row 2: Font size slider (inline value) */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Font size
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {field.fontSize}px
          </Typography>
        </Box>
        <Slider
          value={field.fontSize}
          min={16}
          max={96}
          step={2}
          onChange={(_e, val) => onUpdateField(selectedField, { fontSize: val as number })}
          size="small"
        />
      </Box>

      {/* Row 3: Style + alignment */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Bold">
          <Button
            variant={field.fontWeight === 'bold' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() =>
              onUpdateField(selectedField, {
                fontWeight: field.fontWeight === 'bold' ? 'normal' : 'bold',
              })
            }
            sx={{ minWidth: 36, fontWeight: 'bold', fontFamily: 'serif' }}
          >
            B
          </Button>
        </Tooltip>
        <Box sx={{ width: 8 }} />
        {(['left', 'center', 'right'] as const).map((align) => (
          <Tooltip key={align} title={`Align ${align}`}>
            <Button
              variant={field.textAlign === align ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              onClick={() => onUpdateField(selectedField, { textAlign: align })}
              sx={{ minWidth: 36, textTransform: 'capitalize', px: 0 }}
            >
              {align === 'left' ? '⇤' : align === 'center' ? '↔' : '⇥'}
            </Button>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}
